import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Calendar, Sparkles, PartyPopper, Heart, ArrowRight, MapPin, Champagne, ChevronLeft, ChevronRight, Cake, Gift, Zap, Filter, X, Clock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CALENDAR_CONFIG, DEMO_EVENTS } from '../config/calendarConfig';
import { fetchAllCalendarEvents } from '../lib/googleCalendar';
import '../styles/events.css';

const EventsPage = () => {
  // Video refs
  const heroVideoRef = useRef(null);
  const collabVideoRef = useRef(null);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedDay, setExpandedDay] = useState(null); // For the popup
  
  // Ensure videos play on mount and when visible
  useEffect(() => {
    const playVideos = () => {
      if (heroVideoRef.current) {
        heroVideoRef.current.play().catch(e => console.log('Hero video play prevented:', e));
      }
      if (collabVideoRef.current) {
        collabVideoRef.current.play().catch(e => console.log('Collab video play prevented:', e));
      }
    };
    
    // Play immediately
    playVideos();
    
    // Also play on visibility change (when user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) playVideos();
    });
    
    return () => {
      document.removeEventListener('visibilitychange', playVideos);
    };
  }, []);
  
  // Fetch calendar events from backend API or use demo data
  useEffect(() => {
    const loadEvents = async () => {
      setIsLoading(true);
      
      try {
        // Fetch from backend API
        const events = await fetchAllCalendarEvents();
        
        if (events && events.length > 0) {
          setCalendarEvents(events);
        } else if (CALENDAR_CONFIG.demoMode) {
          // Fall back to demo events if no events from API
          const today = new Date();
          const demoEvents = DEMO_EVENTS.map(event => {
            const eventDate = new Date(event.date);
            eventDate.setFullYear(today.getFullYear());
            eventDate.setMonth(today.getMonth());
            
            if (eventDate < today) {
              eventDate.setMonth(eventDate.getMonth() + 1);
            }
            
            const year = eventDate.getFullYear();
            const month = String(eventDate.getMonth() + 1).padStart(2, '0');
            const day = String(eventDate.getDate()).padStart(2, '0');
            
            return {
              ...event,
              date: `${year}-${month}-${day}`,
              type: event.type === 'kids' ? 'aerial' : event.type
            };
          });
          setCalendarEvents(demoEvents);
        }
      } catch (error) {
        console.error('Error loading events:', error);
        // Fall back to demo events on error
        if (CALENDAR_CONFIG.demoMode) {
          const today = new Date();
          const demoEvents = DEMO_EVENTS.map(event => {
            const eventDate = new Date(event.date);
            eventDate.setFullYear(today.getFullYear());
            eventDate.setMonth(today.getMonth());
            
            if (eventDate < today) {
              eventDate.setMonth(eventDate.getMonth() + 1);
            }
            
            const year = eventDate.getFullYear();
            const month = String(eventDate.getMonth() + 1).padStart(2, '0');
            const day = String(eventDate.getDate()).padStart(2, '0');
            
            return {
              ...event,
              date: `${year}-${month}-${day}`,
              type: event.type === 'kids' ? 'aerial' : event.type
            };
          });
          setCalendarEvents(demoEvents);
        }
      }
      
      setIsLoading(false);
    };
    
    loadEvents();
  }, []);

  // Filter events by category
  const filteredEvents = activeFilter === 'all' 
    ? calendarEvents 
    : calendarEvents.filter(event => event.type === activeFilter);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getEventsForDate = (dateKey) => {
    return filteredEvents.filter(event => event.date === dateKey);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get event type label for display
  const getTypeLabel = (type) => {
    switch(type) {
      case 'bungee': return 'Bungee';
      case 'aerial': return 'Aerial Kids';
      case 'activation': return 'Start Here';
      case 'popup': return 'Pop-Up';
      case 'event': return 'Event';
      default: return type;
    }
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedDay && !e.target.closest('.day-popup') && !e.target.closest('.calendar-day')) {
        setExpandedDay(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedDay]);

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(dateKey);
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
      const hasEvents = dayEvents.length > 0;

      const handleDayClick = (e) => {
        e.stopPropagation();
        if (hasEvents) {
          setExpandedDay(expandedDay === dateKey ? null : dateKey);
        }
      };

      days.push(
        <div 
          key={day} 
          className={`calendar-day ${hasEvents ? 'has-events clickable' : ''} ${isToday ? 'today' : ''}`}
          onClick={handleDayClick}
          role={hasEvents ? "button" : undefined}
          tabIndex={hasEvents ? 0 : undefined}
        >
          <span className="day-number">{day}</span>
          {hasEvents && (
            <div className="day-events" onClick={handleDayClick}>
              {dayEvents.slice(0, 2).map((event, idx) => (
                <div 
                  key={idx} 
                  className={`event-pill ${event.type}`}
                  title={`${event.title} - ${event.time}`}
                  onClick={handleDayClick}
                >
                  <span className="event-preview">{event.title}</span>
                </div>
              ))}
              {dayEvents.length > 2 && (
                <div className="more-events-btn" onClick={handleDayClick}>
                  +{dayEvents.length - 2}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  // Get events for the expanded day
  const getExpandedDayEvents = () => {
    if (!expandedDay) return [];
    return getEventsForDate(expandedDay);
  };

  const expandedDayEvents = getExpandedDayEvents();
  const expandedDayNumber = expandedDay ? parseInt(expandedDay.split('-')[2]) : null;

  return (
    <div className="events-page">
      {/* Hero Section - SEO Optimized */}
      <section className="events-hero">
        <div className="events-hero-video">
          <video 
            ref={heroVideoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            preload="auto"
            className="hero-video"
            poster="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/yx415jpj_Cal_pouring%20bubbly.jpg"
          >
            <source src="/images/hero/events_hero.mp4" type="video/mp4" />
          </video>
          <div className="events-hero-overlay"></div>
        </div>
        <div className="events-hero-content">
          <h1 className="events-title">Schedule & Events</h1>
          <p className="events-subtitle">
            Bungee Fitness, Kids Aerial & Private Events in Wilmington, NC
          </p>
        </div>
      </section>

      {/* Interactive Calendar Section - PRIMARY CONVERSION */}
      <section className="events-calendar-section">
        <div className="calendar-container">
          <div className="calendar-header-section">
            <Calendar size={28} className="section-icon" />
            <h2>Book a Class</h2>
            <p>Tap any day to see what's available</p>
          </div>
          
          {/* Category Filters */}
          <div className="calendar-filters">
            <button 
              className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Classes
            </button>
            <button 
              className={`filter-btn bungee ${activeFilter === 'bungee' ? 'active' : ''}`}
              onClick={() => setActiveFilter('bungee')}
            >
              <Sparkles size={16} />
              Bungee
            </button>
            <button 
              className={`filter-btn aerial ${activeFilter === 'aerial' ? 'active' : ''}`}
              onClick={() => setActiveFilter('aerial')}
            >
              <Heart size={16} />
              Aerial Kids
            </button>
          </div>
          
          <div className="calendar-widget">
            {isLoading ? (
              <div className="calendar-loading">
                <div className="loading-spinner"></div>
                <p>Loading schedule...</p>
              </div>
            ) : (
              <>
                <div className="calendar-nav">
                  <button onClick={() => navigateMonth(-1)} className="calendar-nav-btn">
                    <ChevronLeft size={20} />
                  </button>
                  <h3 className="calendar-month">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                  <button onClick={() => navigateMonth(1)} className="calendar-nav-btn">
                    <ChevronRight size={20} />
                  </button>
                </div>
            
                <div className="calendar-grid">
                  <div className="calendar-day-names">
                    {dayNames.map(day => (
                      <div key={day} className="day-name">{day}</div>
                    ))}
                  </div>
                  <div className="calendar-days">
                    {renderCalendar()}
                  </div>
                </div>
            
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot bungee"></span>
                    <span>Bungee Fitness</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot aerial"></span>
                    <span>Aerial Kids</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot popup"></span>
                    <span>Pop-Up</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot event"></span>
                    <span>Event</span>
                  </div>
                </div>

                {/* Popup Modal for expanded day events */}
                {expandedDay && expandedDayEvents.length > 0 && (
                  <div className="day-popup-overlay" onClick={() => setExpandedDay(null)}>
                    <div className="day-popup" onClick={(e) => e.stopPropagation()}>
                      <div className="day-popup-header">
                        <span className="day-popup-date">
                          {monthNames[currentDate.getMonth()]} {expandedDayNumber}
                        </span>
                        <button className="day-popup-close" onClick={() => setExpandedDay(null)}>
                          <X size={16} />
                        </button>
                      </div>
                      <div className="day-popup-events">
                        {expandedDayEvents.map((event, idx) => (
                          <Link 
                            key={idx} 
                            to={event.bookingUrl}
                            className={`popup-event-card ${event.type}`}
                          >
                            <div className="popup-event-type">{getTypeLabel(event.type)}</div>
                            <div className="popup-event-title">{event.title}</div>
                            <div className="popup-event-time">
                              <Clock size={12} />
                              {event.time}{event.endTime ? ` - ${event.endTime}` : ''}
                            </div>
                            <div className="popup-event-book">
                              Book Now <ExternalLink size={12} />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* UPCOMING POP-UP EVENTS — each button auto-hides after its event */}
      {(new Date() < new Date('2026-10-23T18:30:00') || new Date() < new Date('2026-10-30T19:00:00')) && (
        <section className="popup-events-strip" data-testid="popup-events-strip">
          <h3 className="popup-events-title">Upcoming Pop-Up Events</h3>
          <div className="popup-events-buttons">
            {new Date() < new Date('2026-10-23T18:30:00') && (
              <Link to="/book-event" className="popup-event-btn" data-testid="popup-event-trapeze">
                <span className="popup-event-btn-label">Adult Trapeze Pop-up</span>
                <span className="popup-event-btn-note">Thu, Oct 23 · 6:30pm</span>
              </Link>
            )}
            {new Date() < new Date('2026-10-30T19:00:00') && (
              <Link to="/book-event" className="popup-event-btn halloween" data-testid="popup-event-glow">
                <span className="popup-event-btn-label">🎃 Bungee HALLOWEEN Glow Party</span>
                <span className="popup-event-btn-note">Fri, Oct 30 · 5:30 & 7:00pm</span>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* BEYOND THE SCHEDULE - Private Events & Parties */}
      <section className="beyond-schedule">
        <div className="beyond-content">
          <div className="beyond-header">
            <h2>Beyond the Schedule</h2>
            <p className="beyond-subtitle">
              Looking for something special? We do that too.
            </p>
          </div>

          <div className="beyond-grid">
            {/* Birthday Parties */}
            <div className="beyond-card">
              <div className="beyond-card-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/xh6zoq6x_BirdeBirthday.jpeg" 
                  alt="Kids birthday party"
                />
              </div>
              <div className="beyond-card-content">
                <Cake size={24} className="beyond-icon" />
                <h3>Birthday Parties</h3>
                <p>Private aerial adventures for kids. 2 hours of flying, playing, and celebrating.</p>
                <Link to="/book-aerial" className="beyond-link">
                  Book a Party <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Private Events */}
            <div className="beyond-card">
              <div className="beyond-card-image layback">
                <img 
                  src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/pwd8324e_cal%20layback%20in%20lollipop.JPG" 
                  alt="Aerial layback pose at private event"
                />
              </div>
              <div className="beyond-card-content">
                <PartyPopper size={24} className="beyond-icon" />
                <h3>Private Events</h3>
                <p>Bachelorettes, team builds, office parties. We bring the wow to your occasion.</p>
                <Link to="/consulting#contact-form" className="beyond-link">
                  Inquire <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Live Entertainment */}
            <div className="beyond-card">
              <div className="beyond-card-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/94fz3cdk_Cal_pouring%20bubbly.jpg" 
                  alt="Aerial champagne pour at venue"
                />
              </div>
              <div className="beyond-card-content">
                <Sparkles size={24} className="beyond-icon" />
                <h3>Live Entertainment</h3>
                <p>Aerial performances for restaurants, brand activations, and special occasions.</p>
                <Link to="/consulting#contact-form" className="beyond-link">
                  Inquire <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          {/* Packages Coming Soon Teaser */}
          <div className="beyond-packages">
            <div className="packages-teaser">
              <Zap size={20} />
              <span><strong>Coming Soon:</strong> Curated experiences with local partners — think Bungee + SUP, or a full girls' weekend with movement, play, and a little something spicy.</span>
            </div>
          </div>

          {/* Final CTA */}
          <div className="beyond-cta">
            <p>Have something else in mind?</p>
            <Link to="/consulting#contact-form">
              <Button size="lg" className="beyond-cta-btn">
                Let's Talk
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Honeybears Featured Partner */}
      <section className="events-collab">
        <div className="collab-content">
          <div className="collab-text">
            <span className="collab-badge">Featured Partner</span>
            <h2>Honey Bears — Picnics & Provisions</h2>
            <p>
              We teamed up with Honey Bears to bring aerial magic to luxury picnics. 
              Think champagne pours from a lollipop, surprise performances, and moments 
              that stop the scroll.
            </p>
            <p className="collab-cta-text">
              Want to create something unforgettable together?
            </p>
            <Link to="/consulting#contact-form">
              <Button className="collab-cta-btn">
                Let's Talk
              </Button>
            </Link>
          </div>
          <div className="collab-gallery honeybears three-item">
            <div className="collab-media-item main">
              <video 
                ref={collabVideoRef}
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                className="collab-video"
                poster="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/yx415jpj_Cal_pouring%20bubbly.jpg"
              >
                <source src="/images/hero/honeybears_video.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="collab-media-item secondary">
              <img 
                src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/3e7z5sy6_WinniePicnic.JPG" 
                alt="Little girl at Honey Bears luxury picnic"
              />
            </div>
            <div className="collab-media-item accent">
              <img 
                src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/94fz3cdk_Cal_pouring%20bubbly.jpg" 
                alt="Champagne pour from aerial hoop at Honey Bears"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Link */}
      <section className="events-past">
        <div className="past-content">
          <Link to="/gallery" className="past-gallery-link">
            View the Gallery <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
